import ProtectIcon from "@/assets/images/Protect.png";
import BuildIcon from "@/assets/images/build.svg";
import BuyIcon from "@/assets/images/buy-icon.svg";
import NoImage from "@/assets/images/no-image.webp";
import ProtectionIcon from "@/assets/images/protection-blank-icon.svg";
import TruckIcon from "@/assets/images/truck-icon.svg";
import NoResultFound from "@/components/NoResultFound";
import { Button } from "@/components/ui/button";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useCartDetails from "@/hooks/useCartDetails";
import useDeliveryMessage from "@/hooks/useDeliveryMessage";
import useRemoveFromCart from "@/hooks/useRemoveFromCart";
import useRenderProductRatings from "@/hooks/useRenderProductRating";
import GreenCheckIcon from "@/icons/GreenCheckIcon";
import { selectBestRoute } from "@/lib/deliveryUtils";
import {
  cn,
  decodeHTML,
  isWarehouseOpen,
  renderStockBadge,
  roundPercentage,
} from "@/lib/utils";
import { useGetViewedProductsQuery } from "@/redux/apiSlices/ProductListsApi";
import { deliveryApiSlice } from "@/redux/apiSlices/deliveryApi";
import { useUpdateCartMutation } from "@/redux/apiSlices/userCartApi";
import { emptyOrderDetails } from "@/redux/slices/orderSlice";
import { formatCurrency } from "@/utils/formatCurrency";
import { getThumbnailUrl } from "@/utils/getThumbnailUrl";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicImage from "../DynamicImages/DynamicImage";
import Loader from "../Loader";
import { ConfirmationModal } from "../Modal/ConfirmationModal";
import EditAddOnServices from "../ProductDetail/EditAddOnServices/EditAddOnServices";
import EditWarranty from "../ProductDetail/EditWarranty/EditWarranty";
import { DefaultPriceUi } from "../pricesUi/PricesUi";
import { useToast } from "../ui/use-toast";
import CurrentActiveStep from "./CurrentActiveStep";

const Cart = ({ cartItems }) => {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { removeFromCart, isRemoveFromCartLoading, isRemoveFromCartSuccess } =
    useRemoveFromCart();
  const { getCountByProductId } = useCartDetails();
  const [updateCart, { isSuccess, data, isLoading, isError }] =
    useUpdateCartMutation();

  const zipCode =
    useSelector((state) => state?.userLocation?.zipCode) || "94545";

  const { data: deliveryDetailsList } =
    useSelector((state) =>
      deliveryApiSlice.endpoints.getDeliveryRouteList.select({
        zipCode,
      })(state)
    )?.data || {};

  useEffect(() => {
    if (isSuccess && data?.success) {
      toast({
        title: data?.message || "Cart updated successfully",
      });
    }
    if (isError) {
      toast({
        title: "An error occurred while updating the cart",
        variant: "error",
      });
    }
  }, [isSuccess, data, isError]);

  useEffect(() => {
    dispatch(emptyOrderDetails());
  }, []);

  const handleValueChange = async (productId, quantity) => {
    const updateCartPayload = {
      products: [
        {
          productId: Number(productId),
          quantity,
        },
      ],
    };
    await updateCart(updateCartPayload);
  };

  const handleConfirm = (removeToCartPayload) => {
    removeFromCart(removeToCartPayload);
  };

  const { data: recentlyViewedData } = useGetViewedProductsQuery();
  const { customerViewed = [], recentlyViewed = [] } = recentlyViewedData || {};
  useRenderProductRatings(recentlyViewed);

  const handleRecentlyViewed = async (product) => {
    const productId = product?.id;
    const productStatus = product?.status;
    const currentQuantityOfProduct = getCountByProductId({ productId });
    if (
      product &&
      (productStatus === "A" ||
        productStatus === "B" ||
        productStatus === "S") &&
      currentQuantityOfProduct >= 3
    ) {
      toast({
        variant: "destructive",
        title: "Max quantity reached for this product",
      });
      return;
    }
    const updateCartPayload = {
      products: [
        {
          productId: Number(productId),
          quantity: currentQuantityOfProduct + 1,
        },
      ],
    };
    await updateCart(updateCartPayload);
  };
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;

    const updateCount = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap() + 1);
    };

    updateCount();

    api.on("reInit", updateCount);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });

    return () => {
      api.off("reInit", updateCount);
      api.off("select");
    };
  }, [api]);

  // Function to go to a specific slide when clicking a dot
  const goToSlide = (index) => {
    if (api) {
      api.scrollTo(index);
    }
  };
  const combineDisplayPrice = cartItems?.reduce((acc, item) => {
    const { product } = item || {};
    const { price_details } = product || {};
    const { displayPrice } = price_details || {};
    return acc + Number(displayPrice);
  }, 0);
  return (
    <div className="flex-1 lg:w-full">
      <CurrentActiveStep title="Your Shopping Cart" isActive="products" />

      {cartItems && cartItems?.length === 0 && (
        <NoResultFound message="No Items in Cart" />
      )}
      {isLoading && <Loader />}

      {cartItems &&
        cartItems?.length > 0 &&
        cartItems?.map((item, index) => {
          const {
            product,
            productId,
            quantity,
            isPickup,
            warrantyPriceRanges,
            selectedWarrantyPriceId,
            selectedInstallationServiceId,
            quoteOnly,
            selectedAdditionalServiceId,
            InstallationServices,
            additionalServices,
            selectedinstallationParts,
            selectedinstallationPartsName,
            isPremium: isPremiumInstallation,
          } = item;

          const {
            isActive: isPremium,
            haulAwayPrice = 0,
            moveAwayPrice = 0,
            haulAway,
            moveAway,
          } = additionalServices || {};

          const {
            price_details,
            isInstallationService,
            freeGift = undefined,
            websiteActive = true,
          } = product;

          const {
            cartPrice,
            crossedPrice,
            displayPrice,
            savingAmount,
            savingPercentage,
          } = price_details;

          const removeToCartPayload = {
            products: [
              {
                productId: Number(productId),
              },
            ],
          };
          const maxQuantity =
            product?.status === "A" ||
            product?.status === "B" ||
            product?.status === "S"
              ? 3
              : 5;
          const { warrantyType, warrantyPrice } = warrantyPriceRanges || {};
          const { categoryId, subCategoryId, seoPdpUrl = "" } = product || {};
          const isPartsAvailable = selectedinstallationParts?.length > 0;

          const deliveryDetails =
            deliveryDetailsList?.find((list) => list.productId === productId) ||
            {};
          const isLessThan300 =
            price_details && price_details?.displayPrice <= 300;
          const isAccessory = categoryId === 546;
          return (
            <div key={index} className={cn(`lg:bg-white lg:mb-4`)}>
              <div
                className={cn(
                  "relative lg:rounded-none lg:px-4  mb-[18px] rounded-[13px] px-[50px] py-[38px] flex gap-[50px] bg-white mlg:gap-[25px] mlg:px-[25px] mlg:py-[20px] lg:gap-[8px] lg:m-0 lg:pb-2"
                )}
              >
                <div>
                  <DynamicImage
                    src={product?.thumbnail || NoImage?.src}
                    width={110}
                    height={140}
                    alt="product-image"
                    className=" h-[143px]  w-auto rounded-lg lg:h-[56px] object-contain"
                  />
                </div>
                <div className="w-full flex flex-col justify-between ">
                  <div className="flex justify-between gap-2 lg:gap-[30px]">
                    {!websiteActive ? (
                      <div className="text-[17px] leading-[20px] text-blue3 max-w-[314px] w-full font-semibold pt-[10px] lg:pt-0 lg:text-[15px]">
                        {decodeHTML(product?.productName)}
                      </div>
                    ) : (
                      <Link
                        href={seoPdpUrl}
                        className="text-[17px] leading-[20px] text-blue3 max-w-[314px] w-full font-semibold pt-[10px] lg:pt-0 lg:text-[15px]"
                      >
                        {decodeHTML(product?.productName)}
                      </Link>
                    )}
                    <div className="flex mb-7 items-end flex-col lg:mb-2 ">
                      <div className="flex items-center lg:flex-wrap lg:justify-end sm:flex-col">
                        <h3 className=" text-[24px] font-semibold text-blue3  leading-[36px] lg:leading-[27px] lg:text-[17px]">
                          {formatCurrency(displayPrice)}
                        </h3>
                        {!!crossedPrice && (
                          <p className="ml-2 text-gray2 text-[15px] line-through leading-[22px] lg:text-[13px] lg:leading-[19px] ">
                            {formatCurrency(crossedPrice)}
                          </p>
                        )}
                      </div>
                      {!!savingAmount && (
                        <div className="sm:text-right">
                          <h5 className=" text-red2  text-[12px] leading-[15px]">
                            Save {formatCurrency(savingAmount)}{" "}
                            {savingPercentage
                              ? roundPercentage(savingPercentage)
                              : ""}
                          </h5>
                        </div>
                      )}
                      {!websiteActive && (
                        <h5 className="pt-4 text-red2  text-[20px] leading-[15px]">
                          Not Available
                        </h5>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between gap-2 items-center flex-wrap">
                    <h5 className=" text-gray6 text-[15px] leading-[22px] lg:text-[12px] lg:leading-[18px]">
                      Model: {product?.sku}
                    </h5>
                    <div className="flex items-center gap-4">
                      {websiteActive && (
                        <>
                          <div className="flex items-center gap-[10px]">
                            <p className=" text-[15px] leading-[22px] text-blue3 lg:text-[12px] lg:leading-[18px]">
                              Qty
                            </p>
                            <Select
                              value={quantity}
                              onValueChange={(selectedQuantity) =>
                                handleValueChange(
                                  Number(productId),
                                  selectedQuantity
                                )
                              }
                            >
                              <SelectTrigger className="min-w-[50px] gap-[5px] border-gray7 rounded-[6px] h-[30px] text-[14px]">
                                <SelectValue placeholder="&nbsp;" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {[...Array(maxQuantity)].map((_, index) => (
                                    <SelectItem key={index} value={index + 1}>
                                      {index + 1}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-[6px]">
                            <p className=" pr-[10px] border-r border-gray7 text-gray6 hidden lg:inline-flex text-[12px] ">
                              Save
                            </p>
                          </div>
                        </>
                      )}
                      <ConfirmationModal
                        message="Are you sure you want to remove this item from the cart?"
                        onConfirm={() => handleConfirm(removeToCartPayload)}
                        websiteActive={websiteActive}
                      />
                      {!websiteActive && (
                        <div className="rounded-lg absolute top-0 left-0 right-0 bottom-0 w-full bg-white opacity-50"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {websiteActive && (
                <div>
                  {freeGift && <FreeGiftProduct freeGift={freeGift} />}
                  {/* Pickup and delivery */}
                  <div className="flex gap-[25px] pl-[25px] mlg:gap-[10px] mlg:pl-[10px] lg:p-0 ">
                    <DynamicImage
                      src={TruckIcon}
                      alt="icon"
                      className=" mt-[30px] w-[21px] h-[25px] lg:hidden"
                    />
                    <div className=" lg:p-4 lg:rounded-none lg:gap-3 lg:flex-col py-6 flex-1 mb-[18px] rounded-[13px] px-8 flex gap-[42px] bg-white  mlg:gap-[25px] ">
                      <div className=" lg:flex gap-[16px]">
                        <DynamicImage
                          src={TruckIcon}
                          alt="icon"
                          className="  w-[21px] h-[25px] hidden lg:flex"
                        />
                        <h4 className=" text-blue3 text-[17px] leading-[25px] font-semibold  w-[138px] lg:text-[16px] lg:leading-[24px]">
                          Pickup/Delivery
                        </h4>
                      </div>

                      <div className="lg:pl-0 lg:border-none pl-[42px] flex-1 border-l border-gray7  ">
                        <ZipCodeHandler
                          deliveryDetails={deliveryDetails}
                          isPickable={
                            !(
                              selectedInstallationServiceId ||
                              selectedAdditionalServiceId ||
                              quoteOnly ||
                              isPartsAvailable
                            )
                          }
                          deliveryType={isPickup ? "pickup" : "delivery"}
                          productId={productId}
                          onDeliveryTypeChange={(payload) => {
                            updateCart({
                              products: [
                                {
                                  quantity,
                                  productId,
                                  ...payload,
                                },
                              ],
                            });
                          }}
                          isPremiumInstallation={isPremiumInstallation}
                          quoteOnly={quoteOnly}
                          displayPrice={displayPrice}
                          combineDisplayPrice={combineDisplayPrice}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Protection Plan */}
                  {!(isLessThan300 || isAccessory) && (
                    <div className="flex gap-[25px] pl-[25px] mlg:gap-[10px] mlg:pl-[10px] lg:p-0  ">
                      <DynamicImage
                        src={ProtectionIcon}
                        alt="icon"
                        className=" mt-[30px] w-[21px] h-[25px] lg:hidden"
                      />

                      <div className="lg:flex-col lg:gap-3 lg:p-4 lg:rounded-none py-6 flex-1 mb-[18px] rounded-[13px] px-8 flex gap-[42px] bg-white  mlg:gap-[25px]">
                        <div className="flex gap-4">
                          <DynamicImage
                            src={ProtectionIcon}
                            alt="icon"
                            className="  w-[21px] h-[25px] lg:inline-flex hidden"
                          />
                          <div>
                            <h4 className=" lg:text-[16px] lg:leading-[24px] text-blue3 text-[17px] leading-[25px] font-semibold w-[138px]">
                              Protection Plan
                            </h4>
                          </div>
                        </div>

                        <div className=" lg:rounded-10 lg:border lg:p-4 pl-[42px] flex-1 border-l border-gray7 gap-[26px] flex items-center justify-between mlg:pl-[25px]">
                          <div className="inline-flex gap-[10px] items-center">
                            {warrantyType && (
                              <>
                                <DynamicImage
                                  src={ProtectIcon}
                                  alt="icon"
                                  className="w-[25px]"
                                />
                                <h4 className=" text-blue3">
                                  {warrantyType} Year Plan{" "}
                                </h4>
                              </>
                            )}
                            <EditWarranty
                              selectedWarrantyId={selectedWarrantyPriceId}
                              productId={productId}
                              categoryId={categoryId}
                              subCategoryId={subCategoryId}
                              quantity={quantity}
                            />
                          </div>
                          {warrantyPrice > 0 && warrantyType > 0 && (
                            <div className="text-[14px] leading-[22px] font-medium lg:text-[13px]">
                              ${warrantyPrice}
                              {warrantyType > 0 && (
                                <p className="text-[12px] leading-[18px] text-gray-500">
                                  $
                                  {(
                                    warrantyPrice /
                                    (warrantyType * 12)
                                  ).toFixed(2)}{" "}
                                  /mo.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Professional Services */}
                  {!isPickup && isInstallationService && (
                    <div className="flex gap-[25px] pl-[25px] mlg:gap-[10px] mlg:pl-[10px] lg:p-0">
                      <DynamicImage
                        src={BuildIcon}
                        alt="icon"
                        className=" mt-[30px] w-[22px] h-[22px] lg:hidden"
                      />
                      <div className=" lg:flex-col lg:gap-3 lg:p-4 lg:rounded-none py-6 flex-1 mb-[18px] rounded-[13px] px-8 flex gap-[42px] bg-white  mlg:gap-[25px]">
                        <div className="flex gap-4">
                          <DynamicImage
                            src={BuildIcon}
                            alt="icon"
                            className=" w-[22px] h-[22px] hidden lg:inline-block"
                          />
                          <h4 className=" text-blue3 text-[17px] leading-[25px] font-semibold w-[138px] lg:w-full">
                            Installation Services and Haul Away
                          </h4>
                        </div>
                        <div className=" lg:rounded-10 lg:border lg:p-4  pl-[42px] flex-1 border-l border-gray7 gap-[26px] flex items-center justify-between mlg:pl-[25px]">
                          <div className="flex  space-y-2 my-1 w-full justify-between gap-2">
                            {(selectedInstallationServiceId ||
                              selectedAdditionalServiceId ||
                              quoteOnly ||
                              isPartsAvailable) && (
                              <div>
                                {selectedInstallationServiceId && (
                                  <div>
                                    Installation Services{" "}
                                    {formatCurrency(
                                      InstallationServices?.installationFee
                                    )}
                                  </div>
                                )}

                                {quoteOnly && (
                                  <div>Installation Quote Request</div>
                                )}

                                {selectedAdditionalServiceId && (
                                  <>
                                    <div>
                                      Additional Services:{" "}
                                      {formatCurrency(
                                        Number(haulAwayPrice) +
                                          Number(moveAwayPrice)
                                      )}
                                    </div>
                                    <div>
                                      {haulAway && "Haul away"}{" "}
                                      {haulAway && moveAway && "/"}{" "}
                                      {moveAway && "Move your old appliance"}
                                    </div>
                                  </>
                                )}
                                {isPartsAvailable > 0 && (
                                  <div>
                                    Installation Parts:{" "}
                                    {formatCurrency(
                                      InstallationServices?.installationFee
                                    )}
                                    <ul className="list-inside list-disc">
                                      {selectedinstallationPartsName?.map(
                                        (partName, index) => (
                                          <li key={index}>{partName}</li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                            <EditAddOnServices
                              selectedInstallationServiceId={
                                selectedInstallationServiceId
                              }
                              selectedAdditionalServiceId={
                                selectedAdditionalServiceId
                              }
                              selectedinstallationParts={
                                selectedinstallationParts
                              }
                              quoteOnly={quoteOnly}
                              categoryName={product?.categoryName}
                              additionalServicesData={additionalServices}
                              selectedWarrantyId={selectedWarrantyPriceId}
                              productId={productId}
                              categoryId={categoryId}
                              subCategoryId={subCategoryId}
                              quantity={quantity}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      {/* Recently Viewed */}
      {cartItems &&
        cartItems?.length > 0 &&
        recentlyViewed &&
        recentlyViewed.length > 0 && (
          <div className="flex gap-[25px] pl-[25px] mlg:gap-[10px] mlg:pl-[10px] lg:p-0 lg:block ">
            <DynamicImage
              src={BuyIcon}
              alt="icon"
              className=" mt-[30px] w-[22px] h-[22px] lg:hidden"
            />

            <div className=" py-6 flex-1 mb-[18px] rounded-[13px] px-8 bg-white lg:rounded-none lg:px-4 ">
              <h4 className=" text-blue3 text-[17px] mb-3 leading-[25px] font-semibold ">
                Recently Viewed
              </h4>
              <div className="max-w-[715px] 1300:max-w-[calc(100vw-585px)] mlg:max-w-[calc(100vw-555px)] lg:max-w-full ">
                <Carousel
                  opts={{ align: "start" }}
                  className="w-full pb-6"
                  setApi={setApi}
                >
                  <CarouselContent className="m-0">
                    {recentlyViewed?.map((product, index) => {
                      const webPriceInactive = product.webPriceInactive;
                      return (
                        <CarouselItem
                          key={index}
                          className="basis-2/7 m-0 1300:basis-3/7"
                        >
                          <div className="px-2 py-[10px] h-full">
                            <div className=" min-h-[378px] border rounded-20 px-[18px] py-[25px] border-gray7  min-w-[240px] flex flex-col justify-between">
                              <Link href={product?.pdpSeoUrl}>
                                <div className="w-144px h-[120px] flex items-center justify-center  mb-3">
                                  <DynamicImage
                                    src={getThumbnailUrl(
                                      product?.productImages
                                    )}
                                    alt="img"
                                    className="w-full h-full object-contain"
                                    width={100}
                                    height={100}
                                  />
                                </div>
                                <div>
                                  <h3 className="min-h-[36px] font-semibold text-[12px] leading-[18px] mb-2 max-w-[250px] two-line-dots ">
                                    {decodeHTML(product?.productName)}
                                  </h3>
                                  {!webPriceInactive && (
                                    <DefaultPriceUi
                                      price_details={product?.price_details}
                                    />
                                  )}
                                  <div
                                    id={`category-snippet-${product?.id}${index}`}
                                    className="flex min-h-[24px]"
                                  />
                                  <div className="flex text-gray6 justify-between text-[12px] leading-[22px] lg:text-[12px] lg:leading-[18px]">
                                    Model: {product?.sku} &nbsp;{" "}
                                    {renderStockBadge(
                                      product?.isInStock,
                                      product?.orderStatus
                                    )}
                                  </div>
                                </div>
                              </Link>
                              {!webPriceInactive && (
                                <Button
                                  onClick={() => handleRecentlyViewed(product)}
                                  className=" uppercase font-medium mt-[24px] h-[38px] text-white bg-blue3 hover:bg-inherit text-[12px] border border-blue3 rounded-[10px] w-full hover:text-blue3"
                                >
                                  ADD TO CART
                                </Button>
                              )}
                              {webPriceInactive && (
                                <Link
                                  href={product?.pdpSeoUrl}
                                  className="uppercase font-medium mt-[24px] h-[38px] text-white bg-blue3 hover:bg-inherit text-[12px] border border-blue3 rounded-[10px] w-full hover:text-blue3 flex items-center justify-center"
                                >
                                  Contact Us To Order
                                </Link>
                              )}
                            </div>
                          </div>
                        </CarouselItem>
                      );
                    })}
                  </CarouselContent>
                  <div className=" grid grid-cols-[1fr_auto] items-center gap-3 lg:pl-2">
                    {/* Clickable Pagination Dots */}
                    <div className="flex justify-center ">
                      {Array.from({ length: count }).map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToSlide(index)}
                          className={`w-full h-1  transition-all ${
                            current === index + 1 ? "bg-black" : "bg-[#D0D0D0]"
                          }`}
                        />
                      ))}
                    </div>
                    {/* Navigation Buttons */}
                    <div className="flex items-center justify-end gap-3">
                      <CarouselPrevious className="ring-transparent bg-transparent relative top-auto right-auto left-auto bottom-auto translate-y-0" />
                      <div className="text-sm text-[#0C2340] font-medium">
                        {current}/{count}
                      </div>
                      <CarouselNext className="ring-transparent bg-transparent relative top-auto right-auto left-auto bottom-auto translate-y-0" />
                    </div>
                  </div>
                </Carousel>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default Cart;

const ZipCodeHandler = ({
  onDeliveryTypeChange = () => {},
  deliveryType: selectedOption = "delivery",
  deliveryDetails,
  isPickable,
  isPremiumInstallation,
  quoteOnly,
  displayPrice,
  combineDisplayPrice,
}) => {
  const {
    deliveryRouteDetails,
    productIsInStock,
    productQuantity,
    soldQuantity,
    status,
    productId,
  } = deliveryDetails || {};

  const {
    route = {},
    deliveryMessage: specialDeliveryMessage,
    pickupMessage,
    pickupStatus,
  } = useMemo(() => {
    if (deliveryRouteDetails?.length) {
      return selectBestRoute(
        deliveryRouteDetails,
        {
          status,
          soldQuantity,
          productQuantity,
          productIsInStock,
          isPremiumInstallation,
          quoteOnly,
          displayPrice,
          combineDisplayPrice,
        },
        productId
      );
    }
  }, [
    deliveryRouteDetails,
    isPremiumInstallation,
    quoteOnly,
    displayPrice,
    combineDisplayPrice,
  ]) || {};

  const { routeClassId, daysActive, disabledDates } = route || {};

  const { deliveryMessage: defaultDeliveryMessage, deliveryDate } =
    useDeliveryMessage({
      routeClassId,
      daysActive,
      disabledDates,
    });

  const deliveryMessage = defaultDeliveryMessage || specialDeliveryMessage;

  const handleOptionClick = (option) => {
    const payload = { isPickup: option !== "delivery" };
    onDeliveryTypeChange(payload);
  };

  const renderCheckIcon = () => (
    <GreenCheckIcon className="absolute right-[-0.4px] top-[-0.5px]" />
  );

  const formatDeliveryDate = (date) => {
    if (!date) return "";

    const deliveryDate = new Date(date);
    const month = String(deliveryDate.getMonth() + 1).padStart(2, "0");
    const day = String(deliveryDate.getDate()).padStart(2, "0");
    const year = deliveryDate.getFullYear();

    return `${month}/${day}/${year}`;
  };

  return (
    <div>
      <div className=" gap-4 lg:gap-[14px] grid grid-cols-2 gap-y-0 ">
        {/* Pickup */}
        {isPickable && (
          <div className="relative w-full  lg:min-w-[auto] lg:max-w-full">
            <div
              onClick={() => {
                if (pickupStatus) return;
                handleOptionClick("pickup");
              }}
              className={cn(
                `cursor-pointer h-full p-5 pt-[20px] pb-[15px] border-2 rounded-10 lg:p-3 `,
                selectedOption === "pickup" ? "border-green1" : "border-gray7",
                pickupStatus && "bg-gray-100 cursor-not-allowed"
              )}
            >
              <h3 className="font-medium text-[18px] leading-[27px] mb-[6px] text-[#252525] lg:text-[14px] lg:leading-[21px] lg:mb-[1px]">
                Pickup At Warehouse
              </h3>
              <p className="text-[12px] leading-[18px] mb-[3px] font-medium text-[#0A223C] lg:text-[12px] lg:leading-[18px] lg:mb-[6px]">
                {pickupStatus
                  ? pickupStatus
                  : isWarehouseOpen()
                  ? "Today"
                  : "Tomorrow"}
              </p>
            </div>
            {selectedOption === "pickup" && renderCheckIcon()}
          </div>
        )}

        {/* Delivery */}
        <div className="relative  w-full  lg:min-w-[auto]">
          <div
            onClick={() => handleOptionClick("delivery")}
            className={cn(
              `cursor-pointer h-full p-5 pt-[20px] pb-[15px] border-2 rounded-10 lg:p-3 xsm:min-h-[110px]`,
              selectedOption === "delivery" ? "border-green1" : "border-gray7"
            )}
          >
            <h3 className="font-medium text-[18px] leading-[27px] mb-[6px] text-[#252525] lg:mb-[1px] lg:text-[14px] lg:leading-[21px]">
              Delivery
            </h3>
            <div>
              <p className="text-[12px] leading-[18px] font-medium mb-[3px] lg:mb-[6px] lg:text-[12px] lg:leading-[18px]">
                {deliveryMessage === "Tomorrow" ? "Next Day" : deliveryMessage}
              </p>
              {deliveryDate && (
                <p className="text-[12px] leading-[18px] font-medium  ">
                  {formatDeliveryDate(deliveryDate)}
                </p>
              )}
            </div>
          </div>
          {selectedOption === "delivery" && renderCheckIcon()}
        </div>
        {isPickable && (
          <p
            className={`text-[14px] leading-[20px] text-blue3 mt-2 pl-5 lg:pl-3 ${
              productIsInStock ? "" : "invisible"
            }`}
          >
            Available For Pickup At Hayward Warehouse
          </p>
        )}
      </div>
    </div>
  );
};

const FreeGiftProduct = ({ freeGift }) => {
  const { ProductName, SKU, productImages = [], displayPrice } = freeGift || {};
  const thumbnail = productImages[0]?.thumbnail;

  return (
    <div className="lg:rounded-none lg:px-4  mb-[18px] rounded-[13px] px-[50px] py-[38px] flex gap-[50px] bg-white mlg:gap-[25px] mlg:px-[25px] mlg:py-[20px] lg:gap-[8px] lg:m-0 lg:pb-2">
      <DynamicImage
        src={thumbnail || NoImage?.src}
        width={110}
        height={140}
        alt="product-image"
        className=" max-h-[143px] h-full max-w-[120px] rounded-lg lg:h-[56px] object-contain"
      />
      <div className="w-full flex  justify-between ">
        <div className="flex flex-col  gap-2 ">
          <p className="text-[17px] leading-[20px] text-blue3 max-w-[314px] w-full font-semibold pt-[10px] lg:pt-0 lg:text-[15px]">
            {decodeHTML(ProductName)}
          </p>
          <div className="flex justify-between gap-2 items-center flex-wrap">
            <h5 className=" text-gray6 text-[15px] leading-[22px] lg:text-[12px] lg:leading-[18px]">
              Model: {SKU}
            </h5>
          </div>
        </div>

        <div className="flex mb-7 items-end flex-col lg:mb-2 ">
          <div className="flex items-center lg:flex-wrap lg:justify-end sm:flex-col">
            <h3 className=" text-[24px] font-semibold text-blue3  leading-[36px] lg:leading-[27px] lg:text-[17px]">
              Free
            </h3>
            <p className="ml-2 text-gray2 text-[15px] line-through leading-[22px] lg:text-[13px] lg:leading-[19px] ">
              {formatCurrency(displayPrice)}
            </p>
          </div>
          <div className="sm:text-right">
            <h5 className=" text-red2  text-[12px] leading-[15px]">
              Save {formatCurrency(displayPrice)} (100%)
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};
